// ABOUTME: Particle voice visualizer — geometry/physics from grid_cube_bin_sequence.html.
// Renders as a fixed overlay. Morphs between shapes driven by voice state.
// State → shape: idle=flat-grid, listening=cube-wobble, thinking=cube-fast, speaking=bin

import { useEffect, useRef } from 'react';

// ─── Shape builders (identical to HTML prototype) ─────────────────────────────

const CS = [-1, -1/3, 1/3, 1];

function buildCubePoints() {
  let pts = [];
  CS.forEach(u => CS.forEach(v => {
    pts.push([u, v, -1]); pts.push([u, v,  1]);
    pts.push([-1, u, v]); pts.push([ 1, u, v]);
    pts.push([u, -1, v]); pts.push([u,  1, v]);
  }));
  const seen = new Set();
  return pts.filter(p => {
    const k = p.map(n => n.toFixed(3)).join(',');
    if (seen.has(k)) return false;
    seen.add(k);
    return true;
  });
}

function buildFlatPoints(n) {
  return Array.from({ length: n }, (_, i) => {
    const gi = i % 16;
    return [CS[gi % 4], CS[Math.floor(gi / 4)], 0];
  });
}

const BIN_SEGS = [
  [[-0.15,-1.42],[ 0.15,-1.42]], [[-0.15,-1.42],[-0.15,-1.28]],
  [[ 0.15,-1.42],[ 0.15,-1.28]], [[-0.90,-1.15],[ 0.90,-1.15]],
  [[-0.75,-1.05],[ 0.75,-1.05]], [[-0.75,-1.05],[-0.55, 1.15]],
  [[ 0.75,-1.05],[ 0.55, 1.15]], [[-0.55, 1.15],[ 0.55, 1.15]],
  [[-0.32,-0.85],[-0.24, 0.90]], [[ 0.00,-0.85],[ 0.00, 0.90]],
  [[ 0.32,-0.85],[ 0.24, 0.90]],
];

function buildBinPoints(n) {
  const lens = BIN_SEGS.map(([a,b]) => Math.hypot(b[0]-a[0], b[1]-a[1]));
  const total = lens.reduce((s,l) => s+l, 0);
  return Array.from({ length: n }, (_, i) => {
    let t = (i / n) * total, j = 0;
    while (j < BIN_SEGS.length - 1 && t > lens[j]) { t -= lens[j]; j++; }
    const [a,b] = BIN_SEGS[j];
    const f = lens[j] > 0 ? t / lens[j] : 0;
    return [a[0]+(b[0]-a[0])*f, a[1]+(b[1]-a[1])*f, 0];
  });
}

// ─── Math (identical to HTML prototype) ──────────────────────────────────────

function ease(t) { return t < 0.5 ? 2*t*t : 1 - Math.pow(-2*t+2, 2)/2; }

function rotate([x,y,z], ax, ay) {
  const cosA=Math.cos(ax), sinA=Math.sin(ax);
  const y1=y*cosA-z*sinA, z1=y*sinA+z*cosA;
  const cosB=Math.cos(ay), sinB=Math.sin(ay);
  return [x*cosB+z1*sinB, y1, -x*sinB+z1*cosB];
}

function lerp3(a, b, t) {
  return [a[0]+(b[0]-a[0])*t, a[1]+(b[1]-a[1])*t, a[2]+(b[2]-a[2])*t];
}

// ─── State → animation config ─────────────────────────────────────────────────

// Not every state the shared resolver emits has geometry here. `resolveVisualizerState` also
// returns 'cart' and 'wishlist' (reaction states, which the Cavalry rig has real shapes for and
// this pill does not), so a lookup miss is a normal runtime case, not a programming error — see
// the guard in the frame loop, which holds the current shape rather than morphing to nothing.
const TARGET_SHAPE = {
  idle:      'flat',
  listening: 'cube',
  thinking:  'cube',
  speaking:  'bin',
};
const DEFAULT_SHAPE = TARGET_SHAPE.idle;

/**
 * Decides the next morph when the state changes. Pure, and exported so the shape bookkeeping can be
 * asserted directly — the interesting failure here is a WRONG SHAPE, not a throw, and a wrong shape
 * is invisible from outside a 2D canvas being driven by requestAnimationFrame.
 *
 * `morph.to` is the shape on screen in both cases that matter: the in-flight destination while a
 * morph runs, and the settled shape once it has completed. Reading the previous *state* instead was
 * the bug — states with no geometry ('cart', 'wishlist') resolved to the idle shape, so a
 * 'cart' -> 'idle' step compared idle to idle, started no morph, and stranded the pill on whatever
 * it happened to be drawing before the reaction.
 */
export function nextMorph(morph, state, ts) {
  const toShape = TARGET_SHAPE[state];
  // No geometry for this state. Hold what is on screen rather than morphing to nothing:
  // `SHAPES[undefined]` is undefined and indexing it throws inside the rAF callback, which kills
  // the loop for the rest of the session.
  if (!toShape) return morph;
  if (morph.to === toShape) return morph;
  return { from: morph.to, to: toShape, startTs: ts, progress: 0 };
}

function getRotation(state, ms) {
  if (state === 'listening')
    return { ax: 0.26+0.08*Math.sin(ms*0.0018), ay: 0.36+0.10*Math.sin(ms*0.0013+1) };
  if (state === 'thinking')
    return { ax: 0.40+0.18*Math.sin(ms*0.0032), ay: 0.55+0.22*Math.sin(ms*0.0024+0.5) };
  // 'idle' became the dominant state on 2026-09-01 — before that the resolver never emitted it and
  // the pill's resting state was 'listening' (a wobbling cube). Without an arm here, idle falls to
  // the zero-rotation default over the static flat grid, so once the springs settle the canvas is a
  // frozen image for most of a session. A slow drift, well under the listening wobble, keeps it
  // alive without competing with the states that are meant to read as active.
  if (state === 'idle')
    return { ax: 0.10+0.04*Math.sin(ms*0.0007), ay: 0.14+0.05*Math.sin(ms*0.0005+0.7) };
  return { ax: 0, ay: 0 };
}

// ─── Physics constants (identical to HTML prototype) ─────────────────────────

const STIFFNESS  = 0.16;
const DAMPING    = 0.72;
const SCALE      = 110;
const PERSP_DIST = 4.5;
const DOT_R      = 9;
const MIN_DIST   = DOT_R * 2.3;
const REPEL      = 0.5;
const MORPH_MS   = 800;

// ─── Component ────────────────────────────────────────────────────────────────

export default function ParticlePill({ state = 'idle', size = 320 }) {
  const canvasRef = useRef(null);
  const stateRef  = useRef(state);

  useEffect(() => { stateRef.current = state; }, [state]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const W = canvas.width, H = canvas.height;

    const cubePoints = buildCubePoints();
    const N          = cubePoints.length;
    const flatPoints = buildFlatPoints(N);
    const binPoints  = buildBinPoints(N);
    const SHAPES     = { flat: flatPoints, cube: cubePoints, bin: binPoints };

    const px = new Array(N).fill(W/2), py = new Array(N).fill(H/2);
    const vx = new Array(N).fill(0),   vy = new Array(N).fill(0);

    const initialShape = TARGET_SHAPE[stateRef.current] ?? DEFAULT_SHAPE;
    let morph    = { from: initialShape, to: initialShape, startTs: 0, progress: 1 };
    let prevState = stateRef.current;
    let elapsed  = 0, lastTs = null, rafId;

    function frame(ts) {
      const dt = lastTs != null ? ts - lastTs : 16;
      lastTs = ts;
      elapsed += dt;

      const cur = stateRef.current;

      // Kick off a morph when state changes shape target
      if (cur !== prevState) {
        morph = nextMorph(morph, cur, ts);
        prevState = cur;
      }

      if (morph.progress < 1)
        morph.progress = Math.min(1, (ts - morph.startTs) / MORPH_MS);

      const morphP = ease(morph.progress);
      const srcPts = SHAPES[morph.from];
      const dstPts = SHAPES[morph.to];
      const { ax, ay } = getRotation(cur, elapsed);

      // Spring targets
      const targets = Array.from({ length: N }, (_, i) => {
        const m = lerp3(srcPts[i], dstPts[i], morphP);
        const [rx,ry,rz] = rotate(m, ax, ay);
        const f = PERSP_DIST / (PERSP_DIST - rz);
        return { x: W/2+rx*SCALE*f, y: H/2+ry*SCALE*f, z: rz, r: DOT_R*f };
      });

      // Spring attraction
      const fx = new Array(N).fill(0), fy = new Array(N).fill(0);
      for (let i=0; i<N; i++) {
        fx[i] += (targets[i].x - px[i]) * STIFFNESS;
        fy[i] += (targets[i].y - py[i]) * STIFFNESS;
      }
      // Repulsion
      for (let i=0; i<N; i++) for (let j=i+1; j<N; j++) {
        const dx=px[j]-px[i], dy=py[j]-py[i];
        const d=Math.hypot(dx,dy)||0.001;
        if (d < MIN_DIST) {
          const push=((MIN_DIST-d)/MIN_DIST)*REPEL, nx=dx/d, ny=dy/d;
          fx[i]-=nx*push; fy[i]-=ny*push;
          fx[j]+=nx*push; fy[j]+=ny*push;
        }
      }
      // Integrate
      for (let i=0; i<N; i++) {
        vx[i]=(vx[i]+fx[i])*DAMPING; vy[i]=(vy[i]+fy[i])*DAMPING;
        px[i]+=vx[i]; py[i]+=vy[i];
      }

      // Render back-to-front
      ctx.clearRect(0, 0, W, H);
      targets.map((tg,i) => ({i, z:tg.z}))
        .sort((a,b) => a.z-b.z)
        .forEach(({i}) => {
          ctx.beginPath();
          ctx.arc(px[i], py[i], targets[i].r, 0, Math.PI*2);
          ctx.fillStyle = '#ffffff';
          ctx.fill();
        });

      rafId = requestAnimationFrame(frame);
    }

    rafId = requestAnimationFrame(frame);
    return () => cancelAnimationFrame(rafId);
  }, []); // state flows through ref — no restart needed

  return (
    <canvas
      ref={canvasRef}
      width={440}
      height={440}
      style={{ width: `${size}px`, height: `${size}px`, display: 'block' }}
    />
  );
}
